import { Bell, CheckCircle, Circle } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Notification } from "@/types/notification";
import { useRef, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { generateNotificationLink } from "@/utils/notificationUtils";

// Add CSS animation styles
const animationStyles = `
  @keyframes slideOutRight {
    from {
      transform: translateX(0);
      opacity: 1;
      scale: 1;
    }
    to {
      transform: translateX(100%);
      opacity: 0;
      scale: 0.95;
    }
  }
`;

// Inject styles into document head
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.textContent = animationStyles;
  if (!document.head.querySelector('style[data-notification-animations]')) {
    styleSheet.setAttribute('data-notification-animations', 'true');
    document.head.appendChild(styleSheet);
  }
}

interface NotificationsPopoverProps {
  notifications: Notification[];
  onMarkAllRead: () => void;
  onNotificationClick: (id: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onBellClick: () => void;
}

export function NotificationsPopover({
  notifications,
  onMarkAllRead,
  onNotificationClick,
  open,
  onOpenChange,
  onBellClick
}: NotificationsPopoverProps) {
  const unreadCount = notifications.filter(n => !n.read).length;
  const popoverTriggerRef = useRef<HTMLButtonElement | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Animation state for bulk operations
  const [animatingOut, setAnimatingOut] = useState<Set<string>>(new Set());
  const [previousUnreadCount, setPreviousUnreadCount] = useState(unreadCount);
  const [notificationsToAnimate, setNotificationsToAnimate] = useState<Notification[]>([]);

  // Track individually animating notifications to prevent multiple clicks
  const [individualAnimating, setIndividualAnimating] = useState<Set<string>>(new Set());

  // Trigger animation when "Mark All as Read" is called
  const handleMarkAllReadWithAnimation = () => {
    const unreadNotifications = notifications.filter(n => !n.read);
    if (unreadNotifications.length > 0) {
      // Capture notifications to animate
      setNotificationsToAnimate(unreadNotifications);
      setAnimatingOut(new Set());

      // Start staggered animation
      unreadNotifications.forEach((notification, index) => {
        setTimeout(() => {
          setAnimatingOut(prev => new Set([...prev, notification.id]));
        }, index * 100); // 100ms delay between each notification
      });

      // After animations start, call the actual mark all as read
      setTimeout(() => {
        onMarkAllRead();
      }, 200); // Give time for animations to be visible

      // Clear all animations after they complete
      setTimeout(() => {
        setAnimatingOut(new Set());
        setNotificationsToAnimate([]);
      }, unreadNotifications.length * 100 + 800); // Wait for all animations plus extra time
    } else {
      onMarkAllRead(); // No notifications to animate
    }
  };

  // Function to handle navigation (clicking on notification body)
  function handleNotificationBodyClick(id: string, notification?: Notification) {
    // Start animation for this specific notification
    setAnimatingOut(prev => new Set([...prev, id]));

    // Handle navigation and modal logic immediately but with slight delay
    setTimeout(() => {
      // Handle project-related notifications by opening modal in place
      if (notification) {
        const metadata = notification.metadata || {};
        const isProjectNotification = [
          'project_created', 'project_assigned', 'project_stage_move', 'project_update',
          'project_comment', 'project_mention', 'project_follower',
          'task_assigned', 'task_updated', 'task_completed', 'task_reopened', 'task_comment', 'task_created', 'task_due',
          'comment', 'mention', 'tag'
        ].includes(notification.type);

        if (isProjectNotification && metadata.project_id) {
          // Open project modal in place without navigation

          // Determine the comment tab for communication notifications
          let commentTab = undefined;
          if (getTabForNotificationType(notification.type) === 'communication' && metadata.message_type) {
            commentTab = metadata.message_type === 'internal' ? 'internal' : 'external';
          }

          const tab = getTabForNotificationType(notification.type);
          const eventDetail = {
            projectId: metadata.project_id,
            // Open specific tab based on notification type
            tab: tab,
            commentTab: commentTab
          };

          window.dispatchEvent(new CustomEvent('openProjectModal', {
            detail: eventDetail
          }));
        } else {
          // For non-project notifications, generate the appropriate link
          const linkObj = generateNotificationLink(notification as any);

          if (linkObj) {
            if (linkObj.external) {
              window.open(linkObj.url, '_blank');
            } else {
              // Add a timestamp parameter to force navigation even if already on the same page
              const separator = linkObj.url.includes('?') ? '&' : '?';
              const uniqueLink = `${linkObj.url}${separator}t=${Date.now()}`;
              navigate(uniqueLink);
            }
          }
        }
      }
    }, 100); // Short delay for animation to start

    // Call the parent handler to mark as read after animation shows
    setTimeout(() => {
      onNotificationClick(id);
    }, 150);

    // Close the popover after animation completes
    setTimeout(() => {
      onOpenChange(false);
    }, 300);
  }

  // Function to handle just marking as read (no navigation)
  function handleMarkAsRead(id: string) {
    // Prevent multiple clicks on the same notification
    if (individualAnimating.has(id)) {
      return;
    }

    // Mark as individually animating
    setIndividualAnimating(prev => new Set([...prev, id]));

    // Get the DOM element and apply CSS animation directly
    const notificationElement = document.querySelector(`[data-notification-id="${id}"]`) as HTMLElement;
    if (notificationElement) {
      notificationElement.style.animation = 'slideOutRight 400ms cubic-bezier(0.4, 0, 0.2, 1) forwards';
      notificationElement.style.pointerEvents = 'none';
    }

    // Mark as read and update state after animation
    setTimeout(() => {
      onNotificationClick(id);

      // Clear from animating set
      setIndividualAnimating(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }, 400); // After animation completes
  }

  const getTabForNotificationType = (type: string): string | undefined => {
    switch (type) {
      case 'project_comment':
      case 'project_mention':
      case 'comment':
      case 'mention':
      case 'tag':
        return 'communication';
      case 'task_assigned':
      case 'task_updated':
      case 'task_completed':
      case 'task_reopened':
      case 'task_comment':
      case 'task_created':
      case 'task_due':
        return 'project-tasks'; // Changed from 'tasks' to 'project-tasks'
      default:
        return undefined; // Opens default tab
    }
  };



  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button 
          ref={popoverTriggerRef} 
          variant="ghost"
          size="icon"
          className="relative"
          onClick={onBellClick}
        >
          <Bell className="h-6 w-6 text-foreground" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 flex items-center justify-center h-5 w-5 rounded-full bg-primary text-white text-xs font-bold border border-white">
              {unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        align="end" 
        className="w-96 max-h-96 overflow-y-auto p-0 shadow-lg"
        onWheel={(e) => {
          // Allow wheel scrolling within this container
          e.stopPropagation();
        }}
      >
        <div className="flex items-center justify-between px-3 py-2 border-b border-border">
          <span className="font-semibold text-foreground">Notifications</span>
          {unreadCount > 0 && (
            <Button size="sm" variant="outline" onClick={handleMarkAllReadWithAnimation}>
              Mark all as read
            </Button>
          )}
        </div>
        <div className="divide-y divide-border">
          {(() => {
            const unreadNotifications = notifications.filter(n => !n.read);

            // For bulk animation, use captured notifications; otherwise use current unread
            const notificationsToShow = notificationsToAnimate.length > 0
              ? notificationsToAnimate
              : unreadNotifications;

            return notificationsToShow.length === 0 ? (
              <div className="p-3 text-muted-foreground text-sm text-center">No unread notifications</div>
            ) : (
              notificationsToShow.slice(0, 20).map((n) => {
            // Only log project_comment notifications to reduce noise
            if (n.type === 'project_comment') {

            }

            return (
            <TooltipProvider key={n.id}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    data-notification-id={n.id}
                    className={`flex items-start gap-2 px-3 py-2 hover:bg-muted ${
                      n.read ? '' : 'bg-primary/5 border-l-2 border-primary'
                    }`}
                  >
                    {n.read ? (
                      <CheckCircle className="h-4 w-4 text-muted-foreground mt-0.5" />
                    ) : (
                      <span
                        className="cursor-pointer hover:text-primary-foreground hover:bg-primary rounded-full p-0.5 transition-colors"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleMarkAsRead(n.id);
                        }}
                        title="Mark as read"
                      >
                        <Circle className="h-4 w-4 text-primary mt-0.5" />
                      </span>
                    )}
                    <div
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => handleNotificationBodyClick(n.id, n)}
                    >
                      <div className={`text-sm font-medium truncate ${n.read ? 'text-muted-foreground' : 'text-foreground'}`}>
                        {n.type === 'tag' ? (
                          <>
                            <span className="font-semibold">{n.sender_name}</span> tagged you in <span className="font-semibold">{n.project_name}</span>
                          </>
                        ) : n.title}
                      </div>
                      {n.message && (
                        <div className={`text-xs mt-1 line-clamp-2 ${n.read ? 'text-muted-foreground/80' : 'text-muted-foreground'}`}>
                          {n.message}
                        </div>
                      )}
                      <div className={`text-xs mt-0.5 ${n.read ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
                        {formatDistanceToNow(new Date(n.created_at), {
                          addSuffix: true,
                        })}
                      </div>
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">{n.message}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
              })
            );
          })()}
        </div>
        <Separator className="my-0.5" />
        <div className="px-3 py-1.5 text-center">
          <span
            className="text-sm text-primary font-semibold hover:underline cursor-pointer"
            onClick={() => {
              onOpenChange(false);
              navigate('/app/notifications');
            }}
          >
            See all notifications
          </span>
        </div>
      </PopoverContent>
    </Popover>
  );
}
